import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import {
  AdminFilterSelect,
  AdminPageHeader,
  AdminTableCard,
  useAdminList,
} from './AdminUi';
import './admin.css';

const RECIPIENT_FILTERS = [
  { value: '', label: 'All recipients' },
  { value: 'user', label: 'Patients' },
  { value: 'doctor', label: 'Doctors' },
];

const READ_FILTERS = [
  { value: '', label: 'All status' },
  { value: 'false', label: 'Unread' },
  { value: 'true', label: 'Read' },
];

const TYPE_FILTERS = [
  { value: '', label: 'All types' },
  { value: 'GENERAL', label: 'General' },
  { value: 'APPOINTMENT_BOOKED', label: 'Appointment Booked' },
  { value: 'APPOINTMENT_ACCEPTED', label: 'Appointment Accepted' },
  { value: 'APPOINTMENT_REJECTED', label: 'Appointment Rejected' },
  { value: 'APPOINTMENT_CANCELLED', label: 'Appointment Cancelled' },
  { value: 'APPOINTMENT_RESCHEDULED', label: 'Appointment Rescheduled' },
  { value: 'APPOINTMENT_REMINDER', label: 'Appointment Reminder' },
  { value: 'PAYMENT_SUCCESS', label: 'Payment Success' },
  { value: 'PAYMENT_FAILED', label: 'Payment Failed' },
  { value: 'NEW_MESSAGE', label: 'New Message' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Everyone (patients + doctors)' },
  { value: 'all_users', label: 'All patients' },
  { value: 'all_doctors', label: 'All doctors' },
  { value: 'user', label: 'One patient' },
  { value: 'doctor', label: 'One doctor' },
];

const EMPTY_SEND = {
  audience: 'all',
  user_id: '',
  doctor_id: '',
  title: '',
  message: '',
  notification_type: 'GENERAL',
  force: true,
};

export default function AdminNotifications() {
  const [recipientType, setRecipientType] = useState('');
  const [isRead, setIsRead] = useState('');
  const [nType, setNType] = useState('');
  const [q, setQ] = useState('');
  const [showSend, setShowSend] = useState(false);
  const [form, setForm] = useState(EMPTY_SEND);
  const [sending, setSending] = useState(false);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const { data, loading, error, load } = useAdminList(
    () =>
      api.get('admin/notifications/', {
        params: {
          recipient_type: recipientType || undefined,
          is_read: isRead || undefined,
          notification_type: nType || undefined,
          q: q || undefined,
          limit: 100,
        },
      }),
    [recipientType, isRead, nType]
  );

  useEffect(() => {
    if (!showSend) return undefined;
    let alive = true;
    (async () => {
      try {
        const [uRes, dRes] = await Promise.all([
          api.get('admin/users/', { params: { limit: 200 } }),
          api.get('admin/doctors/', { params: { limit: 200 } }),
        ]);
        if (!alive) return;
        setPatients(uRes.data?.results || []);
        setDoctors(dRes.data?.results || []);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [showSend]);

  const onFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const closeSend = () => {
    setShowSend(false);
    setForm(EMPTY_SEND);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        audience: form.audience,
        title: form.title.trim(),
        message: form.message.trim(),
        notification_type: form.notification_type,
        force: form.force,
      };
      if (form.audience === 'user') payload.user_id = Number(form.user_id);
      if (form.audience === 'doctor') payload.doctor_id = Number(form.doctor_id);

      const res = await api.post('admin/notifications/broadcast/', payload);
      toast.success(res.data?.msg || 'Notification sent');
      closeSend();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (n) => {
    const label = n.title || `#${n.id}`;
    if (!window.confirm(`Delete notification "${label}"?`)) return;
    try {
      await api.delete(`admin/notifications/${n.id}/`);
      toast.success('Notification deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const unread = data.unread ?? 0;

  return (
    <div>
      <AdminPageHeader
        title="Notifications"
        subtitle={`${data.total} total · ${unread} unread`}
      >
        <div className="d-flex gap-2 flex-wrap align-items-center justify-content-end">
          <button
            type="button"
            className="btn btn-primary rounded-pill px-3"
            onClick={() => setShowSend(true)}
          >
            <i className="bi bi-send-plus me-1" />
            Send Notification
          </button>
          <form
            className="d-flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
          >
            <input
              className="form-control"
              style={{ minWidth: 160, maxWidth: 220 }}
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className="btn btn-outline-primary rounded-pill px-3" type="submit">
              Search
            </button>
          </form>
        </div>
      </AdminPageHeader>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <AdminFilterSelect
          value={recipientType}
          onChange={setRecipientType}
          options={RECIPIENT_FILTERS}
          placeholder="All recipients"
          aria-label="Filter by recipient"
        />
        <AdminFilterSelect
          value={isRead}
          onChange={setIsRead}
          options={READ_FILTERS}
          placeholder="All status"
          aria-label="Filter by read status"
        />
        <AdminFilterSelect
          value={nType}
          onChange={setNType}
          options={TYPE_FILTERS}
          placeholder="All types"
          aria-label="Filter by type"
        />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      ) : data.results.length === 0 ? (
        <div className="admin-card p-4 text-center text-muted">No notifications found</div>
      ) : (
        <AdminTableCard>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Recipient</th>
                <th>Notification</th>
                <th>Type</th>
                <th>Status</th>
                <th>Sent</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((n) => (
                <tr key={n.id}>
                  <td>{n.id}</td>
                  <td>
                    <div className="fw-semibold text-truncate" style={{ maxWidth: 160 }}>
                      {n.recipient_name || '—'}
                    </div>
                    <div className="small text-muted">
                      {n.recipient_type === 'doctor' ? 'Doctor' : 'Patient'}
                      {n.recipient_email ? ` · ${n.recipient_email}` : ''}
                    </div>
                  </td>
                  <td>
                    <div className="fw-semibold text-truncate" style={{ maxWidth: 240 }}>
                      {n.title}
                    </div>
                    <div className="small text-muted text-truncate" style={{ maxWidth: 280 }}>
                      {n.message}
                    </div>
                  </td>
                  <td>
                    <span className="admin-badge default">{n.notification_type}</span>
                  </td>
                  <td>
                    <span className={`admin-badge ${n.is_read ? 'approved' : 'pending'}`}>
                      {n.is_read ? 'Read' : 'Unread'}
                    </span>
                  </td>
                  <td className="small text-muted">
                    {n.created_at ? new Date(n.created_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <div className="admin-row-actions justify-content-end">
                      <button
                        type="button"
                        className="admin-action-btn reject"
                        title="Delete"
                        onClick={() => handleDelete(n)}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableCard>
      )}

      {showSend &&
        createPortal(
          <div className="admin-add-doctor-overlay" onClick={closeSend} role="presentation">
            <div
              className="admin-add-doctor-dialog"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="send-notification-title"
            >
              <form onSubmit={handleSend} className="admin-add-doctor-form">
                <div className="admin-add-doctor-header">
                  <div>
                    <h5 id="send-notification-title" className="fw-bold mb-0">
                      Send Notification
                    </h5>
                    <p className="small text-muted mb-0">
                      Broadcast to patients, doctors, or a single recipient
                    </p>
                  </div>
                  <button type="button" className="btn-close" onClick={closeSend} aria-label="Close" />
                </div>

                <div className="admin-add-doctor-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Audience *</label>
                      <select
                        className="form-select"
                        name="audience"
                        value={form.audience}
                        onChange={onFormChange}
                        required
                      >
                        {AUDIENCE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {form.audience === 'user' && (
                      <div className="col-12">
                        <label className="form-label small fw-semibold">Patient *</label>
                        <select
                          className="form-select"
                          name="user_id"
                          value={form.user_id}
                          onChange={onFormChange}
                          required
                        >
                          <option value="">Select patient</option>
                          {patients.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {form.audience === 'doctor' && (
                      <div className="col-12">
                        <label className="form-label small fw-semibold">Doctor *</label>
                        <select
                          className="form-select"
                          name="doctor_id"
                          value={form.doctor_id}
                          onChange={onFormChange}
                          required
                        >
                          <option value="">Select doctor</option>
                          {doctors.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name} ({d.email})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Type</label>
                      <select
                        className="form-select"
                        name="notification_type"
                        value={form.notification_type}
                        onChange={onFormChange}
                      >
                        {TYPE_FILTERS.filter((t) => t.value).map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 d-flex align-items-end">
                      <label className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="force"
                          checked={form.force}
                          onChange={onFormChange}
                        />
                        <span className="form-check-label small">
                          Send even if notifications are disabled
                        </span>
                      </label>
                    </div>

                    <div className="col-12">
                      <label className="form-label small fw-semibold">Title *</label>
                      <input
                        className="form-control"
                        name="title"
                        value={form.title}
                        onChange={onFormChange}
                        maxLength={200}
                        required
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label small fw-semibold">Message *</label>
                      <textarea
                        className="form-control"
                        name="message"
                        rows={4}
                        value={form.message}
                        onChange={onFormChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="admin-add-doctor-footer">
                  <button type="button" className="btn btn-light rounded-pill px-3" onClick={closeSend}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={sending}>
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
